import {
  type AdvertiserAudienceV3,
  ALL_BASE_USERS_AUDIENCE,
  type AudienceV3,
  type AudienceV3ErrorReport,
  type AudienceV3Status,
  type LookalikeAudienceV3,
  type MediaDataRoomParticipant,
  RuleBasedAudienceCombinatorOperator,
  RuleBasedAudienceCondition,
  type RuleBasedAudienceFilters,
  RuleBasedAudienceFiltersBooleanOperator,
  type RuleBasedAudienceV3,
  type SeedAudienceV3,
} from "features/mediaDataRoom/models";
import type * as raw from "./models";

const mapFilterOperatorToAudienceCondition: Record<
  raw.FilterOperator,
  RuleBasedAudienceCondition
> = {
  CONTAINS_ALL_OF: RuleBasedAudienceCondition.CONTAINS_ALL_OF,
  CONTAINS_ANY_OF: RuleBasedAudienceCondition.CONTAINS_ANY_OF,
  CONTAINS_NONE_OF: RuleBasedAudienceCondition.CONTAINS_NONE_OF,
  EMPTY: RuleBasedAudienceCondition.EMPTY,
  NOT_EMPTY: RuleBasedAudienceCondition.NOT_EMPTY,
};

const mapCombineOperatorToAudienceCombinatorOperator: Record<
  raw.CombineOperator,
  RuleBasedAudienceCombinatorOperator
> = {
  DIFF: RuleBasedAudienceCombinatorOperator.DIFF,
  INTERSECT: RuleBasedAudienceCombinatorOperator.INTERSECT,
  UNION: RuleBasedAudienceCombinatorOperator.UNION,
};

const mapBooleanOpToAudienceFiltersOperator: Record<
  raw.BooleanOp,
  RuleBasedAudienceFiltersBooleanOperator
> = {
  AND: RuleBasedAudienceFiltersBooleanOperator.AND,
  OR: RuleBasedAudienceFiltersBooleanOperator.OR,
};

const mapDdcAudienceFiltersToAudienceFilters = (
  filters: raw.AudienceFilters
): RuleBasedAudienceFilters => ({
  filters: filters.filters.map(({ attribute, operator, values }) => ({
    attribute,
    condition: mapFilterOperatorToAudienceCondition[operator],
    values: values ?? null,
  })),
  operator: mapBooleanOpToAudienceFiltersOperator[filters.booleanOp],
});

const mapStalenessWarningsToErrorReport = (
  stalenessWarnings: raw.CustomAudienceStalenessWarnings | null
): AudienceV3ErrorReport | null => {
  if (!stalenessWarnings) {
    return null;
  }
  const missingCustomAudiences = [
    ...new Set(stalenessWarnings?.missingCustomAudiences),
  ];
  const missingSeedAudiences = [
    ...new Set(stalenessWarnings?.missingSeedAudiences),
  ];
  const missingFilterAttributes = [
    ...new Set(stalenessWarnings?.missingFilterAttributes),
  ];
  const missingFilterValues = [
    ...new Set(stalenessWarnings?.missingFilterValues),
  ];
  const fullMessage = [
    ...(missingSeedAudiences.length > 0 || missingCustomAudiences.length > 0
      ? [
          `This audience depends on ${[...missingSeedAudiences, ...missingCustomAudiences].join(", ")} which ${missingSeedAudiences.length + missingCustomAudiences.length === 1 ? "doesn’t" : "don’t"} exist anymore in the latest data.`,
        ]
      : []),
    ...(missingFilterAttributes.length > 0
      ? [
          `This audience uses ${missingFilterAttributes.join(", ")} which ${missingFilterAttributes.length === 1 ? "doesn’t" : "don’t"} exist anymore in the latest data.`,
        ]
      : []),
    ...(missingFilterValues.length > 0
      ? [
          `This audience includes ${missingFilterValues.join(", ")} which ${missingFilterValues.length === 1 ? "doesn’t" : "don’t"} exist anymore in the latest data.`,
        ]
      : []),
  ].join("\n");
  if (!fullMessage) {
    return null;
  }
  return {
    fullMessage,
    missingCustomAudiences,
    missingFilterAttributes,
    missingFilterValues,
    missingSeedAudiences,
  };
};

const mapErrorReportToStalenessWarnings = (
  errorReport: AudienceV3ErrorReport | null
): raw.CustomAudienceStalenessWarnings | undefined => {
  if (!errorReport?.fullMessage) {
    return;
  }
  return {
    missingCustomAudiences: errorReport.missingCustomAudiences,
    missingFilterAttributes: errorReport.missingFilterAttributes,
    missingFilterValues: errorReport.missingFilterValues,
    missingSeedAudiences: errorReport.missingSeedAudiences,
  };
};

export const mapRawAudienceToAudienceV3 = (
  rawAudience: raw.CustomAudience,
  participants: MediaDataRoomParticipant[]
): AudienceV3 => {
  const sharedWith = (rawAudience.sharedWith ?? [])
    .map((id) => participants.find((p) => p.id === id))
    .filter(Boolean) as MediaDataRoomParticipant[];
  const status: AudienceV3Status = rawAudience.sharedWith?.length
    ? "published"
    : "ready";
  const errorReport = mapStalenessWarningsToErrorReport(
    rawAudience.stalenessWarnings ?? null
  );
  const isStale = Boolean(errorReport);
  const currentUserParticipant = participants.find(
    ({ isCurrentUser }) => isCurrentUser
  );
  const canBeViewedByCurrentUser =
    (currentUserParticipant?.emails.includes(rawAudience.createdBy) ||
      rawAudience.sharedWith?.some(
        (participantId) => currentUserParticipant?.id === participantId
      )) ??
    false;
  switch (rawAudience.definition.kind) {
    case "LOOKALIKE": {
      const audience: LookalikeAudienceV3 = {
        createdAt: rawAudience.createdAt,
        createdBy: rawAudience.createdBy,
        doesDependOnLookalikeAudience:
          rawAudience.doesAudienceDependOnLookalikeAudience ?? false,
        excludeSeedAudience:
          rawAudience.definition.excludeSeedAudience ?? false,
        id: rawAudience.id,
        kind: "lookalike",
        name: rawAudience.name,
        reach: rawAudience.definition.reach,
        sharedWith,
        ...mapAudienceReferenceToSourceRef(rawAudience.definition.sourceRef),
        canBeViewedByCurrentUser,
        errorReport,
        isStale,
        status,
      };
      return audience;
    }
    case "REMARKETING": {
      const audience: AdvertiserAudienceV3 = {
        canBeViewedByCurrentUser,
        createdAt: rawAudience.createdAt,
        createdBy: rawAudience.createdBy,
        doesDependOnLookalikeAudience: false,
        errorReport,
        id: rawAudience.id,
        isStale,
        kind: "advertiser",
        name: rawAudience.name,
        sharedWith,
        sourceRef: rawAudience.definition.seedAudienceRef.audienceType,
        sourceRefKind: "seed",
        status,
        type: rawAudience.id,
      };
      return audience;
    }
    case "RULE_BASED": {
      const audience: RuleBasedAudienceV3 = {
        canBeViewedByCurrentUser,
        combine: rawAudience.definition.combine
          ? rawAudience.definition.combine.map(
              ({ filters, sourceRef, operator }) => ({
                filters: filters
                  ? mapDdcAudienceFiltersToAudienceFilters(filters)
                  : null,
                operator:
                  mapCombineOperatorToAudienceCombinatorOperator[operator],
                sourceRef:
                  sourceRef.kind === "BASE"
                    ? ALL_BASE_USERS_AUDIENCE.id
                    : ((sourceRef.id ?? sourceRef.audienceType) as string),
              })
            )
          : null,
        createdAt: rawAudience.createdAt,
        createdBy: rawAudience.createdBy,
        doesDependOnLookalikeAudience:
          rawAudience.doesAudienceDependOnLookalikeAudience ?? false,
        errorReport,
        filters: rawAudience.definition.filters
          ? mapDdcAudienceFiltersToAudienceFilters(
              rawAudience.definition.filters
            )
          : null,
        id: rawAudience.id,
        isStale,
        kind: "rulebased",
        name: rawAudience.name,
        sharedWith,
        ...mapAudienceReferenceToSourceRef(rawAudience.definition.sourceRef),
        status,
      };
      return audience;
    }
  }
};

export const mapRawSeedAudienceToSeedAudienceV3 = (
  rawSeedAudience: raw.SeedAudience
): SeedAudienceV3 => ({
  id: rawSeedAudience.audienceType,
  isEmpty: rawSeedAudience.isEmpty,
  kind: "seed",
  name: rawSeedAudience.audienceType,
});

export type AudienceSourceRef = Pick<
  LookalikeAudienceV3 | RuleBasedAudienceV3 | AdvertiserAudienceV3,
  "sourceRef" | "sourceRefKind"
>;

export const mapAudienceReferenceToSourceRef = (
  audienceReference: raw.AudienceReference
): AudienceSourceRef => {
  switch (audienceReference.kind) {
    case "BASE":
      return {
        sourceRef: ALL_BASE_USERS_AUDIENCE.id,
        sourceRefKind: "base",
      };
    case "CUSTOM":
      return {
        sourceRef: audienceReference.id,
        sourceRefKind: "custom",
      };
    case "SEED":
      return {
        sourceRef: audienceReference.audienceType,
        sourceRefKind: "seed",
      };
  }
};

const mapSourceRefToAudienceReference = (
  sourceRef: AudienceSourceRef
): raw.AudienceReference => {
  switch (sourceRef.sourceRefKind) {
    case "base":
      return {
        kind: "BASE",
      };
    case "custom":
      return {
        id: sourceRef.sourceRef,
        kind: "CUSTOM",
      };
    case "seed":
      return {
        audienceType: sourceRef.sourceRef,
        kind: "SEED",
      };
  }
};

const mapAudienceFiltersOperatorToBooleanOp: Record<
  RuleBasedAudienceFiltersBooleanOperator,
  raw.BooleanOp
> = {
  [RuleBasedAudienceFiltersBooleanOperator.AND]: "AND",
  [RuleBasedAudienceFiltersBooleanOperator.OR]: "OR",
};

const mapAudienceConditionToFilterOperator: Record<
  RuleBasedAudienceCondition,
  raw.FilterOperator
> = {
  [RuleBasedAudienceCondition.CONTAINS_ANY_OF]: "CONTAINS_ANY_OF",
  [RuleBasedAudienceCondition.CONTAINS_NONE_OF]: "CONTAINS_NONE_OF",
  [RuleBasedAudienceCondition.CONTAINS_ALL_OF]: "CONTAINS_ALL_OF",
  [RuleBasedAudienceCondition.EMPTY]: "EMPTY",
  [RuleBasedAudienceCondition.NOT_EMPTY]: "NOT_EMPTY",
};

const mapAudienceCombinatorOperatorToCombineOperator: Record<
  RuleBasedAudienceCombinatorOperator,
  raw.CombineOperator
> = {
  [RuleBasedAudienceCombinatorOperator.INTERSECT]: "INTERSECT",
  [RuleBasedAudienceCombinatorOperator.UNION]: "UNION",
  [RuleBasedAudienceCombinatorOperator.DIFF]: "DIFF",
};

const mapAudienceFiltersToDdcAudienceFilters = (
  filters: RuleBasedAudienceFilters
): raw.AudienceFilters => ({
  booleanOp: mapAudienceFiltersOperatorToBooleanOp[filters.operator],
  filters: filters.filters.map(({ condition, attribute, values }) => ({
    attribute: attribute ?? "",
    operator:
      mapAudienceConditionToFilterOperator[
        condition ?? RuleBasedAudienceCondition.CONTAINS_ANY_OF
      ],
    values: values ?? undefined,
  })),
});

export const mapAudienceV3ToRawAudience = (
  audience: AudienceV3,
  getRawAudienceReferenceById: (id: string) => raw.AudienceReference | undefined
): raw.CustomAudience => {
  switch (audience.kind) {
    case "advertiser": {
      const rawAudience: raw.CustomAudience = {
        createdAt: audience.createdAt ?? new Date().toISOString(),
        createdBy: audience.createdBy,
        definition: {
          kind: "REMARKETING",
          seedAudienceRef: {
            audienceType: audience.sourceRef,
          },
        },
        doesAudienceDependOnLookalikeAudience:
          audience.doesDependOnLookalikeAudience,
        id: audience.id,
        kind: "CUSTOM",
        name: audience.name,
        sharedWith: audience.sharedWith.map(({ id }) => id),
        stalenessWarnings: mapErrorReportToStalenessWarnings(
          audience.errorReport
        ),
      };
      return rawAudience;
    }
    case "lookalike": {
      const rawAudience: raw.CustomAudience = {
        createdAt: audience.createdAt ?? new Date().toISOString(),
        createdBy: audience.createdBy,
        definition: {
          excludeSeedAudience: audience.excludeSeedAudience,
          kind: "LOOKALIKE",
          reach: audience.reach,
          sourceRef: mapSourceRefToAudienceReference(audience),
        },
        doesAudienceDependOnLookalikeAudience:
          audience.doesDependOnLookalikeAudience,
        id: audience.id,
        kind: "CUSTOM",
        name: audience.name,
        sharedWith: audience.sharedWith.map(({ id }) => id),
        stalenessWarnings: mapErrorReportToStalenessWarnings(
          audience.errorReport
        ),
      };
      return rawAudience;
    }
    case "rulebased": {
      const rawAudience: raw.CustomAudience = {
        createdAt: audience.createdAt ?? new Date().toISOString(),
        createdBy: audience.createdBy,
        definition: {
          combine: audience.combine
            ? audience.combine.map(({ filters, operator, sourceRef }) => ({
                filters: filters
                  ? mapAudienceFiltersToDdcAudienceFilters(filters)
                  : undefined,
                operator:
                  mapAudienceCombinatorOperatorToCombineOperator[operator],
                sourceRef:
                  sourceRef === ALL_BASE_USERS_AUDIENCE.id
                    ? { kind: "BASE" }
                    : (getRawAudienceReferenceById(sourceRef) ?? {
                        id: sourceRef,
                        kind: "CUSTOM",
                      }),
              }))
            : undefined,
          filters: audience.filters
            ? mapAudienceFiltersToDdcAudienceFilters(audience.filters)
            : undefined,
          kind: "RULE_BASED",
          sourceRef: mapSourceRefToAudienceReference(audience),
        },
        doesAudienceDependOnLookalikeAudience:
          audience.doesDependOnLookalikeAudience,
        id: audience.id,
        kind: "CUSTOM",
        name: audience.name,
        sharedWith: audience.sharedWith.map(({ id }) => id),
        stalenessWarnings: mapErrorReportToStalenessWarnings(
          audience.errorReport
        ),
      };
      return rawAudience;
    }
  }
};

export const getRawAudienceReference = (
  audience: AudienceV3 | SeedAudienceV3
): raw.AudienceReference => {
  switch (audience.kind) {
    case "seed":
      return {
        audienceType: audience.id,
        kind: "SEED",
      };
    case "advertiser":
    case "lookalike":
    case "rulebased":
      return {
        id: audience.id,
        kind: "CUSTOM",
      };
  }
};

export const getAudienceSourceRef = (
  audience: AudienceV3 | SeedAudienceV3
): AudienceSourceRef => {
  switch (audience.kind) {
    case "lookalike":
    case "rulebased":
    case "advertiser":
      return {
        sourceRef: audience.id,
        sourceRefKind: "custom",
      };
    case "seed": {
      return {
        sourceRef: audience.id,
        sourceRefKind: "seed",
      };
    }
  }
};
